// Hosts dashboard rendering at the sandbox origin, never the parent's,
// so untrusted dashboard code that runs here can't reach the main app's
// DOM or RTK store.
//
// Wire protocol (see sandbox-protocol.ts):
//   sandbox -> parent : { type: 'sandbox-needs-init' }                          (request auth)
//   parent  -> sandbox: { type: 'sandbox-init', apiKey, accessToken,
//                         organizationId, proxyBaseUrl, branch, site }          (auth payload)
//   parent  -> sandbox: { type: 'sandbox-templates', templates }                (registry refresh)
//   parent  -> sandbox: { type: 'sandbox-render', code, theme, props? }
//   sandbox -> parent : { type: 'sandbox-ready' }                               (sent after init)
//   sandbox -> parent : { type: 'sandbox-error', message }
//   sandbox -> parent : { type: 'sandbox-navigate', href, newTab }              (anchor click)
//
// The sandbox is functionally inert until it receives `sandbox-init`.
// Before init: we show a "Waiting for credentials…" state and ignore
// any `sandbox-render` messages. After init: the user's API key and/or
// identity token is stored in the data-runtime module (never read back,
// never persisted) and the live-data hooks fetch the proxy directly.

import { Component, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { applyDocumentColorScheme } from '@axilon-apps/libs-ui-shadcn'

import { getSandboxAuth, setSandboxAuth } from '../data-runtime'
import { installDashboardLinkBridge } from '../navigate-link'
import { compileComponent, loadBabel, type CompileResult } from '../render-runtime'
import { runScript } from '../script-runtime'
import {
  isSandboxInitMessage,
  isSandboxRenderMessage,
  isSandboxRunScriptMessage,
  isSandboxTemplatesMessage,
  type SandboxOutboundMessage,
  type SandboxTheme
} from '../sandbox-protocol'
import { setTemplateRegistry } from './template'

type BabelModule = Awaited<ReturnType<typeof loadBabel>>

const Note = ({ children }: { children: ReactNode }) => (
  <div className="self-center p-6 text-center text-sm text-ink-3">{children}</div>
)

function applyTheme(theme: SandboxTheme): void {
  applyDocumentColorScheme(theme)
}

function postToParent(message: SandboxOutboundMessage): void {
  if (window.parent === window) {
    // Top-level (headless screenshot capture): there is no distinct parent
    // window. Echo the message to ourselves so a controlling script
    // listening on this window (Playwright's page-side driver) still
    // observes lifecycle signals — chiefly `sandbox-initialized`, which it
    // waits for before requesting a render. Our own message handler ignores
    // these outbound types, so the echo is harmless.
    window.postMessage(message, '*')
    return
  }
  // We don't know the exact parent origin (the sandbox is deployable to
  // multiple parent deployments), so we post to '*' — the parent is the
  // one with the secret, and we never send secrets, so the unrestricted
  // origin here is fine.
  window.parent.postMessage(message, '*')
}

interface SandboxState {
  code: string | null
  /**
   * Props spread into the rendered component as `<Component {...props} />`.
   * Used by the Templates editor to preview a template with its stored
   * default values; dashboards leave this an empty object.
   */
  props: Record<string, unknown>
  error: string | null
  /** Bumped when the templates registry is replaced so React remounts. */
  registryEpoch: number
}

const SandboxHost = () => {
  const [babel, setBabel] = useState<BabelModule | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [state, setState] = useState<SandboxState>({
    code: null,
    props: {},
    error: null,
    registryEpoch: 0
  })
  const [initStatus, setInitStatus] = useState<'pending' | 'ready' | 'no-auth'>('pending')

  // The message handler is installed once on mount, so it can't close over
  // the latest `babel` state directly. Mirror it into a ref the handler reads.
  const babelRef = useRef<BabelModule | null>(null)
  babelRef.current = babel

  useEffect(() => installDashboardLinkBridge(document, postToParent), [])

  useEffect(() => {
    let active = true
    loadBabel()
      .then((mod) => {
        if (active) setBabel(mod)
      })
      .catch(() => {
        if (active) setLoadFailed(true)
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    const handle = (event: MessageEvent): void => {
      // In the embedded editor `window.parent` is the host UI; in the
      // headless capture page it is `window` itself (we post to self).
      // Either way, ignore messages from any other source.
      if (event.source !== window.parent && event.source !== window) return
      if (isSandboxInitMessage(event.data)) {
        // At least one explicit credential is required. Surface a dedicated
        // state instead of letting every live-data hook fail with 401.
        const accessToken = event.data.accessToken ?? ''
        if (event.data.apiKey === '' && accessToken === '') {
          setInitStatus('no-auth')
          return
        }
        setSandboxAuth({
          apiKey: event.data.apiKey,
          accessToken,
          organizationId: event.data.organizationId ?? '',
          proxyBaseUrl: event.data.proxyBaseUrl,
          branch: event.data.branch,
          site: event.data.site,
          ...(event.data.dashboardId ? { dashboardId: event.data.dashboardId } : {})
        })
        setInitStatus('ready')
        // Deterministic "auth applied" signal for the capture driver.
        postToParent({ type: 'sandbox-initialized' })
        return
      }
      if (isSandboxTemplatesMessage(event.data)) {
        // Replace the in-process templates registry. The Template component
        // looks entries up by `path`. Bump `registryEpoch` so React
        // remounts the dashboard against the new registry — covers a
        // late-arriving registry after first paint and parent edits.
        setTemplateRegistry(event.data.templates)
        setState((prev) =>
          prev.code === null ? prev : { ...prev, registryEpoch: prev.registryEpoch + 1 }
        )
        return
      }
      if (isSandboxRenderMessage(event.data)) {
        applyTheme(event.data.theme)
        const nextCode = event.data.code
        const nextProps = (event.data.props ?? {}) as Record<string, unknown>
        setState((prev) => ({
          code: nextCode,
          props: nextProps,
          error: null,
          registryEpoch: prev.registryEpoch
        }))
        return
      }
      if (isSandboxRunScriptMessage(event.data)) {
        const { requestId, code } = event.data
        // Scripts fetch live data, so they need credentials just like the
        // hooks do. Bail early with a clear error rather than letting every
        // data call throw inside the script.
        if (getSandboxAuth() === null) {
          postToParent({
            type: 'sandbox-script-result',
            requestId,
            ok: false,
            resultJson: null,
            truncated: false,
            logs: [],
            error: 'Sandbox is not initialized with credentials yet.'
          })
          return
        }
        // Babel may still be loading when the script arrives; `loadBabel`
        // returns the same memoized promise the mount effect awaits.
        void (async () => {
          try {
            const mod = babelRef.current ?? (await loadBabel())
            const result = await runScript(mod, code)
            postToParent({ type: 'sandbox-script-result', requestId, ...result })
          } catch (err) {
            postToParent({
              type: 'sandbox-script-result',
              requestId,
              ok: false,
              resultJson: null,
              truncated: false,
              logs: [],
              error: err instanceof Error ? err.message : String(err)
            })
          }
        })()
        return
      }
    }
    window.addEventListener('message', handle)
    // Ask the parent for credentials. The parent listens for this and
    // responds with `sandbox-init`. We also keep posting `sandbox-ready`
    // so the parent's existing "iframe is up" handler triggers and
    // (re)sends the most recent render payload.
    postToParent({ type: 'sandbox-needs-init' })
    postToParent({ type: 'sandbox-ready' })
    return () => {
      window.removeEventListener('message', handle)
    }
  }, [])

  const compiled = useMemo<CompileResult>(() => {
    if (babel === null || state.code === null) return { Component: null, error: null }
    return compileComponent(babel, state.code)
  }, [babel, state.code])

  // Surface compile errors to the parent so it can render an inline
  // banner alongside the iframe. The empty-string case ('No default-
  // exported component.' when nothing has been written yet) is just the
  // "waiting for code" state, not an error.
  useEffect(() => {
    if (compiled.error === null) return
    postToParent({ type: 'sandbox-error', message: compiled.error })
  }, [compiled.error])

  if (loadFailed) return <Note>Couldn&apos;t load the renderer.</Note>
  if (babel === null) return <Note>Loading…</Note>
  if (initStatus === 'no-auth') {
    return (
      <Note>
        No authentication available. Return to the ontology UI and sign in, or configure an HMAC API
        key under Settings → General when using an API-key deployment.
      </Note>
    )
  }
  if (initStatus === 'pending') return <Note>Waiting for credentials…</Note>
  if (state.code === null) return <Note>Waiting for dashboard…</Note>
  if (compiled.Component === null) {
    return (
      <Note>
        {compiled.error === null
          ? 'Click Configure to add code for this dashboard.'
          : `Couldn't render: ${compiled.error}`}
      </Note>
    )
  }

  // Render-time crashes are caught by the boundary; we re-key it on the
  // code so a previously-thrown boundary resets when the code changes.
  return (
    <RenderBoundary
      resetKey={`${state.registryEpoch}:${state.code}`}
      onError={(message) => postToParent({ type: 'sandbox-error', message })}
    >
      <compiled.Component key={state.registryEpoch} {...state.props} />
    </RenderBoundary>
  )
}

interface RenderBoundaryProps {
  resetKey: string
  onError: (message: string) => void
  children: ReactNode
}

interface RenderBoundaryState {
  hasError: boolean
  message: string
  lastKey: string
}

// Small error boundary so a throwing component doesn't take down the
// whole iframe. Resets whenever `resetKey` (the code) changes.
class RenderBoundary extends Component<RenderBoundaryProps, RenderBoundaryState> {
  constructor(props: RenderBoundaryProps) {
    super(props)
    this.state = { hasError: false, message: '', lastKey: props.resetKey }
  }

  static getDerivedStateFromError(error: unknown): Partial<RenderBoundaryState> {
    return { hasError: true, message: error instanceof Error ? error.message : String(error) }
  }

  static getDerivedStateFromProps(
    props: RenderBoundaryProps,
    state: RenderBoundaryState
  ): Partial<RenderBoundaryState> | null {
    if (props.resetKey !== state.lastKey) {
      return { hasError: false, message: '', lastKey: props.resetKey }
    }
    return null
  }

  override componentDidCatch(error: unknown): void {
    this.props.onError(error instanceof Error ? error.message : String(error))
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <Note>Couldn&apos;t render: {this.state.message}</Note>
    }
    return this.props.children
  }
}

export default SandboxHost
